import React, { useState, useEffect } from 'react';
import { useLocation } from '@reach/router';
import styled from '@emotion/styled';
import { validate, saveForm } from '@greythr/shared-utils/hsAPI';
import ReCAPTCHA from 'react-google-recaptcha';
import { getButtonId } from '../../../../../shared-utils/src/components/utils/common';
import { Box, Grid, Input, Spinner, Flex, Heading, Button, Image } from '@theme-ui/components';

const ShowProcessing = () => (
	<Flex
		sx={{ alignItems: 'center', flexDirection: 'column', mt: 3, height: ['47px', '37px', '37px'] }}
	>
		<Spinner color='#ffffff' />
	</Flex>
);

let recaptchaRef = React.createRef();

const DemoForm = ({ pageCode, pageName, regionCode, form_code, setShowHeading }) => {
	const location = useLocation();
	const pageUrl = location.href;

	const [name, setName] = useState('');
	const [email, setEmail] = useState('');
	const [mobileNumber, setMobileNumber] = useState('');
	const [company, setCompany] = useState('');

	const [emailError, setEmailError] = useState('');
	const [hasError, setHasError] = useState(false);
	const [message, setMessage] = useState('');
	const [processing, setProcessing] = useState(false);

	const checkMobileNoValidity = (e) => {
		e.preventDefault();
		const mobileNoInput = document.getElementById('demo_mobile_input');
		const validityState = mobileNoInput.validity;
		if (validityState.valueMissing) {
			mobileNoInput.setCustomValidity('Please fill in this field.');
		} else if (validityState.tooLong || validityState.tooShort) {
			mobileNoInput.setCustomValidity('Mobile number must be 10 digits long');
		} else if (validityState.patternMismatch) {
			mobileNoInput.setCustomValidity(
				'Mobile number must start with any one of the following digits: (6/7/8/9).'
			);
		} else {
			mobileNoInput.setCustomValidity('');
		}
	};

	const onSubmit = async (e) => {
		e.preventDefault();
		setHasError(false);
		setProcessing(true);
		setMessage('');

		const error = validate(email);
		setEmailError(error);
		if (error) {
			setProcessing(false);
			return;
		}

		if (recaptchaRef && recaptchaRef.current) {
			let captcha = '';
			recaptchaRef.current.reset();
			captcha = await recaptchaRef.current.executeAsync();
			const payload = {
				mat_full_name: name,
				mat_email: email,
				mat_phone: mobileNumber,
				mat_company: company,
				page_code: pageCode,
				page_name: pageName,
				page_url: pageUrl,
				form_code,
				invisible_recaptcha: true,
				captcha,
			};
			if (captcha) {
				saveForm(
					payload,
					(response) => {
						setProcessing(false);
						setShowHeading(false);
						setMessage('Thank you. We have noted your request, we will get back to you!');
					},
					(error) => {
						setProcessing(false);
						if (
							error.response &&
							error.response.status == 400 &&
							error.response.data.invalid_email
						) {
							setEmailError(error.response.data.invalid_email);
						} else {
							setHasError(true);
						}
					}
				);
			} else {
				setProcessing(false);
				setHasError(true);
			}
		} else {
			setProcessing(false);
			setHasError(true);
		}
	};

	return (
		<Box sx={{ textAlign: 'center' }}>
			{message ? (
				<Box
					sx={{
						width: '80%',
						color: '#ffffff',
						fontWeight: 600,
						fontSize: [2, 3],
						mx: 'auto',
						mt: 3,
					}}
				>
					{message}
				</Box>
			) : (
				<Box as='form' sx={{ textAlign: 'center' }} onSubmit={onSubmit}>
					<Grid
						columns={[1, 2, 2, 2]}
						sx={{ gap: 2, maxWidth: '378px', justifyItems: 'center', m: 'auto' }}
					>
						<StyledInput
							type='text'
							placeholder='Full name*'
							value={name}
							name='name'
							required
							maxLength='100'
							onChange={(e) => setName(e.target.value)}
						/>
						<StyledInput
							type='text'
							placeholder='Mobile number*'
							value={mobileNumber}
							name='mobileNumber'
							required
							minLength={10}
							maxLength={10}
							pattern='^[6789][0-9]{9}$'
							id='demo_mobile_input'
							onBlur={checkMobileNoValidity}
							onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, ''))}
						/>
						<StyledInput
							type='email'
							placeholder='Business email*'
							value={email}
							name='email'
							required
							maxLength='100'
							onChange={(e) => setEmail(e.target.value)}
						/>
						<StyledInput
							type='text'
							placeholder='Company name*'
							value={company}
							name='company'
							required
							maxLength='200'
							onChange={(e) => setCompany(e.target.value)}
						/>
					</Grid>
					<ReCAPTCHA
						ref={recaptchaRef}
						size='invisible'
						sitekey={process.env.GATSBY_INVISIBLE_RE_CAPTCHA_SITE_KEY}
						badge='bottomleft'
					/>
					{processing ? (
						<ShowProcessing />
					) : (
						<StyledButton id={getButtonId(`${pageCode}_request_demo`, regionCode)}>
							Request demo
						</StyledButton>
					)}
				</Box>
			)}
			<Box sx={{ textAlign: 'center', color: '#ffffff', mt: 2, height: '21px', mb: [2, 2, 0] }}>
				{emailError ? <b>{emailError}</b> : null}
				{hasError ? <b>Sorry, some error seems to have occurred!</b> : null}
			</Box>
		</Box>
	);
};

const PillarDemoReqBanner = ({ pageCode, pageName, regionCode, data }) => {
	const [showHeading, setShowHeading] = useState(true);
	const [banner, setBanner] = useState();
	const [flexDirection, setFlexDirection] = useState('row');

	useEffect(() => {
		const randomIndex = Math.floor(Math.random() * data.length);
		setBanner(data[randomIndex]);
		if(data[randomIndex]?.imageAlignment === "right") {
			setFlexDirection('row-reverse')
		}
	}, []);

	if (!banner) {
		return null;
	}

	return (
		<Flex
			sx={{
				backgroundColor: '#f58220',
				maxWidth: ['340px', '450px', '714px', '714px'],
				flexDirection: ['column', 'column', flexDirection, flexDirection],
				m: '36px auto',
			}}
		>
			{banner?.image && (
				<Box
					sx={{
						textAlign: ['left', 'center', 'left'],
						minWidth: ['auto', '272px'],
						minHeight: ['329px', '272px', 'auto'],
					}}
				>
					<Image src={banner.image} sx={{ width: ['325px', 'auto'], objectFit: 'cover' }} />
				</Box>
			)}
			<Flex
				sx={{
					flexDirection: 'column',
					alignItems: ['center', 'center', 'end'],
					justifyContent: 'center',
					alignItems: 'center',
					width: ['auto', 'auto', '58%'],
					minHeight: ['409px', '270px', 'auto'],
					m: 'auto',
				}}
			>
				{showHeading && (
					<Heading
						sx={{
							maxWidth: '320px',
							textAlign: 'center',
							fontSize: '23px',
							color: '#ffffff',
							fontWeight: 700,
							my: '1.24rem',
							mx: 1,
						}}
					>
						{banner.heading}
					</Heading>
				)}
				<DemoForm
					pageName={pageName}
					pageCode={pageCode}
					regionCode={regionCode}
					form_code={banner.form_code}
					setShowHeading={setShowHeading}
				/>
			</Flex>
		</Flex>
	);
};

const StyledInput = styled(Input)`
	background: #ffffff;
	border-radius: 4px;
	width: 185px;
	font-size: 15px;
	height: 37px;
	padding: 10px 16px;
	margin: 0px;
	@media (max-width: 639px) {
		width: 243px;
		height: 47px;
	}
`;

const StyledButton = styled(Button)`
	height: 37px;
	width: 206px;
	margin-top: 16px;
	border-radius: 4px;
	background: #ffffff;
	color: #414042;
	font-size: 18px;
	font-style: bold;
	:hover {
		box-shadow: 0 2px 4px 0 #b5b4b4;
		background-color: #ffffff;
	}
	@media (max-width: 639px) {
		width: 267px;
		height: 47px;
	}
`;

export default PillarDemoReqBanner;
